import React from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { Box, Typography, Button } from '@mui/material';
import Header from "components/Header/Header.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import HomeFooter from "components/Footer/HomeFooter.js";
import Download from "../components/Download";
import SeoServices from "../components/SeoServices";
import SeoLinks from "../components/SeoLinks";
import Error from "./Error";
import { colors } from '../components/Theme/WebTheme';
import { FONT_FAMILY } from "../common/sharedFunctions";
import { getCity, getService, fill, serviceCityMeta, serviceCityJsonLd, SITE_URL } from "../config/seoContent";

const dashboardRoutes = [];

// Pagina programatica servicio x ciudad para SEO long-tail:
// "grúa para carro en Medellín", "grúa para moto en Cali", etc.
export default function ServiceCityLanding(props) {
    const { service: serviceSlug, city: citySlug } = useParams();
    const service = getService(serviceSlug);
    const city = getCity(citySlug);
    const { ...rest } = props;

    if (!service || !city) {
        return <Error />;
    }

    const { url, title, description, keywords, h1 } = serviceCityMeta(service, city);
    const [serviceLd, breadcrumb, faq] = serviceCityJsonLd(service, city);

    return (
        <div style={{ backgroundColor: colors.LandingPage_Background, margin: '-8px' }}>
            <Helmet>
                <html lang="es" />
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta name="keywords" content={keywords} />
                <link rel="canonical" href={url} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="es_CO" />
                <meta property="og:url" content={url} />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content={`${SITE_URL}/apple-icon.png`} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <meta name="geo.region" content="CO" />
                <meta name="geo.placename" content={city.name} />
                <script type="application/ld+json">{JSON.stringify(serviceLd)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
                <script type="application/ld+json">{JSON.stringify(faq)}</script>
            </Helmet>

            <Header
                color="transparent"
                routes={dashboardRoutes}
                rightLinks={<HeaderLinks />}
                fixed
                changeColorOnScroll={{ height: 400, color: "white" }}
                {...rest}
            />

            <Box
                component="section"
                sx={{ backgroundColor: '#000', color: '#fff', pt: { xs: 14, md: 18 }, pb: { xs: 6, md: 9 }, px: { xs: 3, md: 8 }, textAlign: 'center' }}
            >
                <Typography component="h1" sx={{ fontFamily: FONT_FAMILY, fontWeight: 'bold', fontSize: { xs: 28, md: 46 }, mb: 2 }}>
                    {h1}
                </Typography>
                <Typography component="p" sx={{ fontFamily: FONT_FAMILY, fontSize: { xs: 16, md: 20 }, color: '#d7d7d7', maxWidth: 900, mx: 'auto', mb: 2 }}>
                    {fill(service.intro, city)}
                </Typography>
                <Typography component="p" sx={{ fontFamily: FONT_FAMILY, fontSize: { xs: 14, md: 16 }, color: '#9e9e9e', maxWidth: 880, mx: 'auto', mb: 4 }}>
                    {fill(service.text, city)}
                </Typography>
                <Button variant="contained" color="warning" href="/login" sx={{ fontFamily: FONT_FAMILY, fontSize: 16, px: 4, py: 1.2 }}>
                    Pedir {service.name.toLowerCase()} en {city.name}
                </Button>
            </Box>

            {/* Preguntas frecuentes (refuerza FAQ schema con contenido visible) */}
            <Box component="section" sx={{ backgroundColor: '#0a0a0a', color: '#fff', px: { xs: 2, md: 8 }, py: { xs: 5, md: 7 } }}>
                <Typography component="h2" sx={{ fontFamily: FONT_FAMILY, fontWeight: 'bold', fontSize: { xs: 22, md: 30 }, mb: 3 }}>
                    Preguntas frecuentes sobre {service.name.toLowerCase()} en {city.name}
                </Typography>
                {(service.faq || []).map((f, i) => (
                    <Box key={i} sx={{ mb: 2.5, maxWidth: 900 }}>
                        <Typography component="h3" sx={{ fontFamily: FONT_FAMILY, fontWeight: 'bold', fontSize: 17, mb: 0.5 }}>
                            {fill(f.q, city)}
                        </Typography>
                        <Typography component="p" sx={{ fontFamily: FONT_FAMILY, fontSize: 15, color: '#bdbdbd', lineHeight: 1.6 }}>
                            {fill(f.a, city)}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <SeoServices />
            <SeoLinks currentCity={city} currentService={service} />
            <Download />
            <HomeFooter />
        </div>
    );
}
